import { tierFactAPI } from "./api";
import { normalizeDateString, dateToIso, addDaysIso, generateAllMonthGroups } from "../TierPlan/services";

export const getResidents = async () => {
  try {
    const res = await tierFactAPI.getResidents();
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getBlocks = async (resident_id) => {
  try {
    if (!resident_id) return [];
    const res = await tierFactAPI.getBlocks(resident_id);
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getWorkGroups = async (block_id) => {
  try {
    if (!block_id) return [];
    const res = await tierFactAPI.getWorkGroups(block_id);
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

// Функция для парсинга даты и извлечения дня и месяца
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Пробуем разные форматы даты
    let date;
    if (dateStr.includes('T')) {
      date = new Date(dateStr);
    } else if (dateStr.includes('.')) {
      // Формат DD.MM.YYYY
      const parts = dateStr.split('.');
      date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    } else if (dateStr.includes('-')) {
      // Формат YYYY-MM-DD
      date = new Date(dateStr);
    } else {
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) return null;
    
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
    
    return {
      original: dateStr,
      day,
      month,
      year,
      monthName: monthNames[month - 1],
      sortKey: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    };
  } catch (e) {
    return null;
  }
};

export const getWorkTaskFact = async (work_group_id, work_group_level_id = null) => {
  try {
    if (!work_group_id) {
      // Если передан work_group_level_id, возвращаем формат для этажей
      if (work_group_level_id) {
        return { data: [] };
      }
      return { matrix: [], columns: [], monthGroups: [] };
    }
    const res = await tierFactAPI.getWorkTaskFact(work_group_id, work_group_level_id);
    const groupedData = res?.data || [];
    
    // Если передан work_group_level_id, возвращаем данные в формате для этажей
    if (work_group_level_id) {
      return { data: groupedData };
    }
    
    // Разворачиваем сгруппированные данные в плоский список (для обычного режима без этажей)
    const flatData = [];
    groupedData.forEach(group => {
      if (group.items && Array.isArray(group.items)) {
        group.items.forEach(item => {
          const normalizedDate = normalizeDateString(item.work_date || item.plan_date || item.fact_date);
          if (normalizedDate) {
            flatData.push({
              ...item,
              task_name_group: group.task_name,
              work_date: normalizedDate
            });
          }
        });
      }
    });
    
    // Определяем минимальную дату с данными для генерации диапазона
    let firstDateWithData = null;
    const datesWithData = flatData
      .filter((item) => {
        const hasPlan = item.plan_value !== null && item.plan_value !== undefined && item.plan_value !== '';
        const hasFact = item.fact_value !== null && item.fact_value !== undefined && item.fact_value !== '';
        return (hasPlan || hasFact) && item.work_date;
      })
      .map((item) => item.work_date)
      .filter(Boolean);
    
    if (datesWithData.length > 0) {
      const sortedDatesWithData = [...new Set(datesWithData)].sort((a, b) =>
        a.localeCompare(b)
      );
      firstDateWithData = sortedDatesWithData[0];
    }
    
    // Генерируем все месяцы с датами, как в режиме с этажами
    let monthGroups = [];
    if (firstDateWithData) {
      const startDate = firstDateWithData;
      const startDateWithPast = addDaysIso(startDate, -365);
      const endDate = addDaysIso(startDate, 10 * 365);
      monthGroups = generateAllMonthGroups(startDateWithPast, endDate);
    } else {
      // Если нет данных, генерируем диапазон вокруг текущей даты
      const today = dateToIso(new Date());
      const startDateWithPast = addDaysIso(today, -365);
      const endDate = addDaysIso(today, 10 * 365);
      monthGroups = generateAllMonthGroups(startDateWithPast, endDate);
    }
    
    // Собираем уникальные task_name
    const uniqueTaskNames = [...new Set(flatData.map(item => item.task_name || item.task_name_group).filter(Boolean))];
    
    // Создаем словарь для быстрого поиска task_dict_id, unit_name и is_edit по task_name
    const taskDictMap = {};
    const unitNameMap = {};
    const isEditMap = {};
    groupedData.forEach(group => {
      if (group.task_name) {
        if (group.task_dict_id) {
          taskDictMap[group.task_name] = group.task_dict_id;
        }
        if (group.unit_name) {
          unitNameMap[group.task_name] = group.unit_name;
        }
        // Сохраняем is_edit для каждой группы (задачи)
        // Если is_edit не указан, по умолчанию считаем, что редактирование разрешено (true)
        isEditMap[group.task_name] = group.is_edit !== undefined ? group.is_edit : true;
      }
    });
    
    // Создаем словарь для быстрого поиска данных по task_name и date
    const taskDateMap = {};
    flatData.forEach((item) => {
      const taskName = item.task_name || item.task_name_group;
      if (!taskName || !item.work_date) return;
      if (!taskDateMap[taskName]) {
        taskDateMap[taskName] = {};
      }
      if (!taskDateMap[taskName][item.work_date]) {
        taskDateMap[taskName][item.work_date] = [];
      }
      // Показываем записи с планом или фактом
      const hasPlan = (item.work_task_plan_id !== null && item.work_task_plan_id !== undefined) || 
                     (item.plan_value !== null && item.plan_value !== undefined && item.plan_value !== '');
      const hasFact = (item.work_task_fact_id !== null && item.work_task_fact_id !== undefined) || 
                     (item.fact_value !== null && item.fact_value !== undefined && item.fact_value !== '');
      if (hasPlan || hasFact) {
        taskDateMap[taskName][item.work_date].push(item);
      }
    });
    
    // Создаем матрицу: task_name по вертикали, все даты всех месяцев по горизонтали
    const matrix = uniqueTaskNames.map(taskName => {
      const row = {
        task_name: taskName,
        task_dict_id: taskDictMap[taskName] || null,
        unit_name: unitNameMap[taskName] || null,
        is_edit: isEditMap[taskName] !== undefined ? isEditMap[taskName] : true
      };
      
      // Для каждой даты из всех месяцев находим соответствующие записи
      monthGroups.forEach(monthGroup => {
        monthGroup.dates.forEach(dateInfo => {
          row[dateInfo.original] = taskDateMap[taskName]?.[dateInfo.original] || [];
        });
      });
      
      return row;
    });
    
    // Собираем все даты из всех месяцев
    const allDates = [];
    monthGroups.forEach(monthGroup => {
      monthGroup.dates.forEach(dateInfo => {
        allDates.push(dateInfo.original);
      });
    });
    
    return {
      matrix,
      columns: allDates,
      monthGroups,
      firstDateWithData
    };
  } catch (e) {
    return { matrix: [], columns: [], monthGroups: [], firstDateWithData: null };
  }
};

// Функция для парсинга даты и извлечения дня
const getDayFromDate = (dateStr) => {
  const parsed = parseDate(dateStr);
  return parsed ? parsed.day : dateStr;
};

export const getWorkTaskFactColumns = (dates, monthGroups) => {
  const columns = [
    {
      name: "task_name",
      label: "Название задачи",
      align: "left",
      field: "task_name",
      sortable: true,
    },
  ];
  
  // Добавляем динамические колонки для каждой даты (только день)
  dates.forEach(date => {
    columns.push({
      name: date,
      label: getDayFromDate(date),
      align: "center",
      field: date,
    });
  });
  
  return columns;
};

export const updateWorkTaskFact = async (data) => {
  try {
    const res = await tierFactAPI.updateWorkTaskFact(data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteWorkTaskFact = async (data) => {
  try {
    const res = await tierFactAPI.deleteWorkTaskFact(data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getWorkGroupLevelNum = async (work_group_id) => {
  try {
    if (!work_group_id) return [];
    const res = await tierFactAPI.getWorkGroupLevelNum(work_group_id);
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getGprValues = async (work_group_id, work_days, work_group_level_id = null) => {
  try {
    if (!work_group_id || !work_days || work_days.length === 0) return {};
    const res = await tierFactAPI.getGprValues(work_group_id, work_days, work_group_level_id);
    return res?.data || {};
  } catch (e) {
    console.error('Error fetching GPR values:', e);
    return {};
  }
};

